import { useEffect, useState } from "react";
import { Download, AlertTriangle, FileSignature, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { downloadFile } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

const fmtQar = (v) => `QAR ${Number(v || 0).toLocaleString()}`;

export const IncrementPlanning = () => {
  const [plan, setPlan] = useState(null);
  const [packLoading, setPackLoading] = useState(false);

  useEffect(() => {
    api.get("/kpi/increments").then((r) => setPlan(r.data));
  }, []);

  const downloadCommitteePack = async () => {
    setPackLoading(true);
    try {
      await downloadFile("/payroll/committee-pack", "increment-committee-briefing.pdf");
      toast.success("Committee briefing pack downloaded");
    } catch {
      toast.error("Committee pack generation failed — check payroll runs exist");
    } finally {
      setPackLoading(false);
    }
  };

  if (!plan) return <Skeleton className="h-96 w-full rounded-none" />;
  const t = plan.totals;

  return (
    <div className="space-y-4" data-testid="increment-planning">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {plan.bands.map((b) => (
            <span key={b.min_score} className="text-[10px] font-mono border border-border bg-white px-2 py-1 text-slate-600">
              ≥{b.min_score} → +{b.increment_pct}%{b.development_plan ? " + dev plan" : ""}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="committee-pack-btn" disabled={packLoading}
            onClick={downloadCommitteePack}
            className="rounded-none h-8 text-[11px] bg-[var(--brand-primary)] hover:bg-slate-700 text-white">
            {packLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSignature className="h-3.5 w-3.5" />}
            {packLoading ? "Assembling pack…" : "Committee Pack"}
          </Button>
          <Button data-testid="increments-pdf-btn" variant="outline"
            onClick={() => downloadFile("/kpi/increments/pdf", "increment-planning.pdf")}
            className="rounded-none h-8 text-[11px] border-teal-600/50 text-teal-700 hover:bg-teal-50">
            <Download className="h-3.5 w-3.5" /> Export PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[["Staff scored", t.headcount, "inc-kpi-headcount"],
          ["Avg score", t.avg_score, "inc-kpi-avg"],
          ["With increment", t.with_increment, "inc-kpi-count"],
          ["Dev plans", t.development_plans, "inc-kpi-devplans"],
          ["Monthly impact", fmtQar(t.monthly_total_qar), "inc-kpi-monthly"],
          ["Annual impact", fmtQar(t.annual_total_qar), "inc-kpi-annual"]].map(([l, v, id]) => (
          <div key={l} className="bg-white border border-border px-4 py-3" data-testid={id}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{l}</p>
            <p className="text-[16px] font-bold text-[var(--brand-primary)] mt-1 font-mono">{v}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-border overflow-x-auto">
        <table className="w-full text-[12px] min-w-[980px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-slate-50">
              {["Employee", "Dept", "Role matrix", "Score", "Recommended", "Monthly Δ", "Annual cost", "Flag"].map((h) => (
                <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {plan.rows.map((r) => (
              <tr key={r.employee_id} data-testid={`increment-row-${r.employee_id}`}
                className={r.development_plan ? "bg-amber-50/50" : ""}>
                <td className="px-4 py-2.5">
                  <p className="font-medium text-slate-800">{r.name}</p>
                  <p className="text-[10px] font-mono text-slate-400">{r.employee_id} · {r.title}</p>
                </td>
                <td className="px-4 py-2.5 text-slate-600">{r.department}</td>
                <td className="px-4 py-2.5 text-slate-500 text-[11px]">{r.role_matrix}</td>
                <td className="px-4 py-2.5 font-mono font-semibold text-[var(--brand-primary)]">{r.weighted_score}</td>
                <td className="px-4 py-2.5 font-mono text-teal-700 font-semibold">+{r.recommended_pct}%</td>
                <td className="px-4 py-2.5 font-mono">{r.monthly_increase_qar ? fmtQar(r.monthly_increase_qar) : "—"}</td>
                <td className="px-4 py-2.5 font-mono">{r.annual_cost_qar ? fmtQar(r.annual_cost_qar) : "—"}</td>
                <td className="px-4 py-2.5">
                  {r.development_plan && (
                    <span className="inline-flex items-center gap-1 text-[9px] font-mono bg-amber-100 border border-amber-300 text-amber-900 px-1.5 py-0.5">
                      <AlertTriangle className="h-3 w-3" /> DEV PLAN
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IncrementPlanning;
